import React, { useState } from 'react';
import { Calendar, Clock, Users, PlusCircle, Check, X, Edit2, Trash2 } from 'lucide-react';

interface Resident {
  name: string;
  age: number;
  address: string;
  emergencyContact: string;
  careLevel: string;
  primaryConditions: string[];
}

interface Caregiver {
  id: string;
  name: string;
  role: string;
  lastAccess: Date;
}

interface CheckInSchedulerProps {
  resident: Resident;
  caregivers: Caregiver[];
}

interface CheckIn {
  id: string;
  caregiverId: string;
  date: Date;
  timeSlot: string;
  type: 'IN_PERSON' | 'VIDEO' | 'PHONE';
  tasks: string[];
  notes: string;
  status: 'SCHEDULED' | 'COMPLETED' | 'MISSED';
  priority: 'HIGH' | 'NORMAL' | 'LOW';
}

// Mock data
const initialCheckIns: CheckIn[] = [
  {
    id: 'checkin-1',
    caregiverId: '3',
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
    timeSlot: '09:00-10:00',
    type: 'IN_PERSON',
    tasks: ['Medication administration', 'Vital signs check', 'Physical therapy assistance'],
    notes: 'Focus on mobility exercises recommended by physical therapist',
    status: 'SCHEDULED',
    priority: 'HIGH'
  },
  {
    id: 'checkin-2',
    caregiverId: '2',
    date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
    timeSlot: '18:00-19:00',
    type: 'VIDEO',
    tasks: ['Medication reminder', 'Daily check-in', 'Social conversation'],
    notes: 'Ensure dinner was prepared properly',
    status: 'SCHEDULED',
    priority: 'NORMAL'
  },
  {
    id: 'checkin-3',
    caregiverId: '4',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    timeSlot: '14:00-15:00',
    type: 'IN_PERSON',
    tasks: ['Mobility assessment', 'Exercises demonstration', 'Gait training'],
    notes: 'Patient showed improvement in balance exercises',
    status: 'COMPLETED',
    priority: 'HIGH'
  }
];

// Mock recommended check-in times based on activity patterns
const recommendedTimes = [
  { 
    timeSlot: '08:30-09:30', 
    reason: 'Low activity detected after waking up - optimal for morning medication administration'
  },
  { 
    timeSlot: '13:00-14:00', 
    reason: 'Consistent pattern of reduced movement detected - may indicate need for assistance with meal preparation'
  },
  { 
    timeSlot: '19:00-20:00', 
    reason: 'Bathroom visits frequently detected during this time - fall risk is elevated'
  }
];

const CheckInScheduler: React.FC<CheckInSchedulerProps> = ({ resident, caregivers }) => {
  const [checkIns, setCheckIns] = useState<CheckIn[]>(initialCheckIns);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingCheckIn, setEditingCheckIn] = useState<CheckIn | null>(null);
  const [showPast, setShowPast] = useState(false);
  
  // Form state for adding/editing check-ins
  const [formData, setFormData] = useState<Partial<CheckIn>>({
    caregiverId: '',
    date: new Date(),
    timeSlot: '',
    type: 'IN_PERSON',
    tasks: [],
    notes: '',
    priority: 'NORMAL'
  });
  
  // New task input state
  const [newTask, setNewTask] = useState('');
  
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      date: new Date(e.target.value)
    });
  };
  
  const handleTaskAdd = () => {
    if (newTask.trim()) {
      setFormData({
        ...formData,
        tasks: [...(formData.tasks || []), newTask.trim()]
      });
      setNewTask('');
    }
  };
  
  const handleTaskRemove = (index: number) => {
    const updatedTasks = [...(formData.tasks || [])];
    updatedTasks.splice(index, 1);
    setFormData({
      ...formData,
      tasks: updatedTasks
    });
  };
  
  const handleAddCheckIn = () => {
    // Reset form and show the add form
    setFormData({
      caregiverId: '',
      date: new Date(),
      timeSlot: '',
      type: 'IN_PERSON',
      tasks: [],
      notes: '',
      priority: 'NORMAL'
    });
    setIsAddingNew(true);
    setEditingCheckIn(null);
  };
  
  const handleEditCheckIn = (checkIn: CheckIn) => {
    setFormData(checkIn);
    setEditingCheckIn(checkIn);
    setIsAddingNew(false);
  };
  
  const handleSaveCheckIn = () => {
    if (!formData.caregiverId || !formData.date || !formData.timeSlot) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (isAddingNew) {
      const newCheckIn: CheckIn = {
        ...formData as Required<Omit<CheckIn, 'id' | 'status'>>,
        id: `checkin-${Date.now()}`,
        status: 'SCHEDULED'
      };
      
      setCheckIns([...checkIns, newCheckIn]);
    } else if (editingCheckIn) {
      const updatedCheckIns = checkIns.map(ci => 
        ci.id === editingCheckIn.id ? { ...formData, id: ci.id, status: ci.status } as CheckIn : ci
      );
      
      setCheckIns(updatedCheckIns);
    }
    
    // Reset form state
    setIsAddingNew(false);
    setEditingCheckIn(null);
  };
  
  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setEditingCheckIn(null);
  };
  
  const handleDeleteCheckIn = (id: string) => {
    if (window.confirm('Are you sure you want to remove this check-in?')) {
      setCheckIns(checkIns.filter(ci => ci.id !== id));
    }
  };
  
  const handleUpdateStatus = (id: string, status: 'SCHEDULED' | 'COMPLETED' | 'MISSED') => {
    setCheckIns(checkIns.map(ci => 
      ci.id === id ? { ...ci, status } : ci
    ));
  };
  
  const handleAddRecommendedTime = (timeSlot: string) => {
    setFormData({
      ...formData,
      timeSlot
    });
  };
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const isPastCheckIn = (date: Date) => {
    return date.getTime() < new Date().setHours(0, 0, 0, 0);
  };
  
  const filteredCheckIns = checkIns.filter(checkIn => 
    showPast || !isPastCheckIn(checkIn.date)
  );
  
  const getCaregiverName = (id: string) => {
    const caregiver = caregivers.find(c => c.id === id);
    return caregiver ? caregiver.name : 'Unknown';
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold">Check-in Scheduler</h3>
          <p className="text-gray-600">Manage and coordinate care visits for {resident.name}</p>
        </div>
        <div className="flex space-x-3">
          <label className="flex items-center text-sm">
            <input 
              type="checkbox" 
              checked={showPast} 
              onChange={() => setShowPast(!showPast)}
              className="mr-2 rounded text-blue-500"
            />
            Show past check-ins
          </label>
          <button
            onClick={handleAddCheckIn}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <PlusCircle size={16} className="mr-1" />
            New Check-in
          </button>
        </div>
      </div>
      
      {/* Add/Edit Form */}
      {(isAddingNew || editingCheckIn) && (
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex justify-between items-center mb-4 pb-2 border-b">
            <h3 className="font-medium">{isAddingNew ? 'Schedule New Check-in' : 'Edit Check-in'}</h3>
            <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caregiver
                </label>
                <select
                  name="caregiverId"
                  value={formData.caregiverId || ''}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                  required
                >
                  <option value="">Select Caregiver</option>
                  {caregivers.map(caregiver => (
                    <option key={caregiver.id} value={caregiver.id}>
                      {caregiver.name} ({caregiver.role})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date ? formData.date.toISOString().split('T')[0] : ''}
                  onChange={handleDateChange}
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time Slot
                </label>
                <input
                  type="text"
                  name="timeSlot"
                  placeholder="e.g., 09:00-10:00"
                  value={formData.timeSlot || ''}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-in Type
                </label>
                <select
                  name="type"
                  value={formData.type || 'IN_PERSON'}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="IN_PERSON">In Person</option>
                  <option value="VIDEO">Video Call</option>
                  <option value="PHONE">Phone Call</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority || 'NORMAL'}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="HIGH">High</option>
                  <option value="NORMAL">Normal</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
            
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tasks
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Add a task"
                    className="flex-1 border rounded-l-lg p-2"
                  />
                  <button
                    onClick={handleTaskAdd}
                    className="bg-blue-500 text-white px-3 rounded-r-lg hover:bg-blue-600"
                  >
                    <PlusCircle size={16} />
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {formData.tasks?.map((task, index) => (
                    <div key={index} className="flex items-center bg-gray-50 p-2 rounded">
                      <span className="flex-1 text-sm">{task}</span>
                      <button
                        onClick={() => handleTaskRemove(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2 h-24"
                  placeholder="Add any specific instructions or notes for this check-in"
                />
              </div>
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg mr-2 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCheckIn}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {isAddingNew ? 'Schedule Check-in' : 'Update Check-in'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Recommended Check-in Times */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="font-medium flex items-center">
            <Clock className="text-blue-500 mr-2" size={18} />
            Recommended Check-in Times
          </h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            These times are automatically suggested based on {resident.name}'s activity patterns 
            detected by floor sensors.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendedTimes.map((rec, index) => (
              <div key={index} className="border rounded-lg p-3 bg-blue-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{rec.timeSlot}</span>
                  <button
                    onClick={() => handleAddRecommendedTime(rec.timeSlot)}
                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                  >
                    Use This Time
                  </button>
                </div>
                <p className="text-sm text-gray-600">{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Scheduled Check-ins */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="font-medium flex items-center">
            <Calendar className="text-green-500 mr-2" size={18} />
            Scheduled Check-ins
          </h3>
        </div>
        
        {filteredCheckIns.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {filteredCheckIns.map(checkIn => (
              <li key={checkIn.id} className={`p-4 ${
                isPastCheckIn(checkIn.date) ? 'bg-gray-50' : ''
              }`}>
                <div className="flex justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      checkIn.type === 'IN_PERSON'
                        ? 'bg-green-100 text-green-600'
                        : checkIn.type === 'VIDEO'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-purple-100 text-purple-600'
                    }`}>
                      {checkIn.type === 'IN_PERSON' ? (
                        <Users size={20} />
                      ) : checkIn.type === 'VIDEO' ? (
                        <div>VC</div>
                      ) : (
                        <div>☎️</div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="font-medium">{getCaregiverName(checkIn.caregiverId)}</h4>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                          checkIn.priority === 'HIGH'
                            ? 'bg-red-100 text-red-800'
                            : checkIn.priority === 'NORMAL'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {checkIn.priority}
                        </span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                          checkIn.status === 'COMPLETED'
                            ? 'bg-green-100 text-green-800'
                            : checkIn.status === 'MISSED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {checkIn.status}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        {formatDate(checkIn.date)} | {checkIn.timeSlot} | {checkIn.type.replace('_', ' ')}
                      </p>
                      
                      {checkIn.tasks.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Tasks:</p>
                          <ul className="text-sm list-disc pl-5 space-y-1">
                            {checkIn.tasks.map((task, index) => (
                              <li key={index}>{task}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {checkIn.notes && (
                        <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                          {checkIn.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    {!isPastCheckIn(checkIn.date) && (
                      <>
                        <button
                          onClick={() => handleEditCheckIn(checkIn)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteCheckIn(checkIn.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    
                    {(checkIn.status === 'SCHEDULED' && new Date(checkIn.date) <= new Date()) && (
                      <button
                        onClick={() => handleUpdateStatus(checkIn.id, 'COMPLETED')}
                        className="text-green-500 hover:text-green-700"
                        title="Mark as completed"
                      >
                        <Check size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No check-ins found. Click "New Check-in" to schedule one.
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInScheduler; 